import { Spin } from 'antd';
import { Suspense, lazy } from 'react';

import { useParams } from '@suntex.core/nav';
import { useModuleById } from '@suntex.fim/framework';
import { modulesPath } from '@suntex.fim/nav';

import { ModuleLayout } from '../components/ModuleLayout';

import { SOUND_DIRECTORY, useModuleViewProps } from './shared';

const SkillSetsView = lazy(() =>
  import('@suntex.modules/skill-sets').then((m) => ({
    default: m.SkillSetsView,
  })),
);

const LAST_MODULE_ID = 24;
const SKILL_SET_LEVEL_COUNT = 8;
const GAMES_PER_LEVEL = 3;

function isValidLevel(n: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= SKILL_SET_LEVEL_COUNT;
}

function isValidGame(n: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= GAMES_PER_LEVEL;
}

function moduleIdFor(level: number, game: number): number {
  return GAMES_PER_LEVEL * (level - 1) + game;
}

function levelGameFor(moduleId: number): { level: number; game: number } {
  const level = Math.floor((moduleId - 1) / GAMES_PER_LEVEL) + 1;
  const game = ((moduleId - 1) % GAMES_PER_LEVEL) + 1;
  return { level, game };
}

export function SkillSets() {
  const params = useParams<{ level: string; game: string }>();
  const viewProps = useModuleViewProps();

  const level = Number(params.level);
  const game = Number(params.game);

  const moduleId =
    isValidLevel(level) && isValidGame(game) ? moduleIdFor(level, game) : null;

  // Hooks must be called unconditionally; pass 0 sentinel when invalid
  const module = useModuleById(moduleId ?? 0);

  if (moduleId === null) {
    return <div>Skill set game not found.</div>;
  }

  if (!module) {
    return <Spin />;
  }

  let nextGameUrl: string | null = null;
  if (moduleId < LAST_MODULE_ID) {
    const next = levelGameFor(moduleId + 1);
    nextGameUrl = `${modulesPath(viewProps.preview)}/ss/${next.level}/${next.game}`;
  }

  return (
    <ModuleLayout moduleId={moduleId}>
      <Suspense fallback={<Spin />}>
        <SkillSetsView
          {...viewProps}
          module={module}
          moduleId={moduleId}
          skillSet={level}
          game={game}
          gameNumber={moduleId}
          soundDirectory={SOUND_DIRECTORY}
          nextGameUrl={nextGameUrl}
        />
      </Suspense>
    </ModuleLayout>
  );
}
